import axiosClient from '@/api/axiosClient';
import API from '@/api/endpoints';
import type {
  ConsoleSettings,
  FolderCountsResponse,
  FolderId,
  FolderPageResponse,
  SessionDto,
  SessionTagsResponse,
  TagDef,
} from '@/types';

/**
 * ADR-033 - the typed fetchers for the folder-based conversation list, the folder counts and the tag catalog. Every
 * one is a thin, defensive wrapper: it asks the server one question and normalises the answer so a proxy's HTML page,
 * a half-deployed server or a malformed field degrades to "nothing" rather than a crash in the rail. They reject on any
 * non-2xx (the caller decides what a failure means) EXCEPT where the contract says a status is an answer (the tag
 * catalog's 404).
 *
 * The console does not decide who may see what: the server applies the visibility rules to every answer here, and the
 * `ownershipEnforced` it returns is the only thing the console keys its privacy rendering on.
 */

/** The server clamps to 1..50 (a value above 200 is a 400 at the controller); the client never asks for more. */
export const MAX_PAGE_SIZE = 50;
/** D13: twenty rows per page per folder. */
export const DEFAULT_PAGE_SIZE = 20;

const clampPageSize = (n: number): number => {
  const v = Number.isFinite(n) ? Math.trunc(n) : DEFAULT_PAGE_SIZE;
  return Math.min(MAX_PAGE_SIZE, Math.max(1, v));
};

const nonBlank = (v: string | null | undefined): string | undefined => {
  const t = (v ?? '').trim();
  return t ? t : undefined;
};

/**
 * One keyset page of ONE folder: `GET /api/sessions?folder=&cursor=&pageSize=&q=&onlyWithMessages=`.
 *
 * `folder` is REQUIRED and must be non-blank: a blank folder with no cursor/q is the server's legacy "every
 * conversation" page mode, which has no cursor and would silently load the whole tenant - so it is refused here rather
 * than sent. `cursor` is the opaque `nextCursor` of the previous page of THIS folder (omitted for the first page); `q`
 * is omitted when blank. Folder ids are case-sensitive and sent verbatim.
 *
 * The answer is normalised: `items` is always an array, `nextCursor` is a non-blank string or null, `hasMore` is true
 * only when there is a cursor to follow (a `hasMore` with no cursor cannot be paged and is read as the end), and
 * `ownershipEnforced` is true ONLY when the server said exactly `true`.
 */
export async function listSessionsByFolder(
  folder: FolderId,
  cursor?: string | null,
  pageSize: number = DEFAULT_PAGE_SIZE,
  q?: string | null,
  onlyWithMessages = true,
): Promise<FolderPageResponse> {
  const folderId = nonBlank(folder);
  if (!folderId) throw new Error('listSessionsByFolder: a folder id is required');

  const params: Record<string, string | number | boolean> = {
    folder: folderId,
    pageSize: clampPageSize(pageSize),
    onlyWithMessages: onlyWithMessages === true,
  };
  const c = nonBlank(cursor);
  if (c) params.cursor = c;
  const query = nonBlank(q);
  if (query) params.q = query;

  const res = await axiosClient.get<Partial<FolderPageResponse> | null>(API.SESSIONS.LIST, { params });
  return normaliseFolderPage(res.data);
}

function normaliseFolderPage(data: Partial<FolderPageResponse> | null | undefined): FolderPageResponse {
  const body = (data ?? {}) as Partial<FolderPageResponse>;
  const items: SessionDto[] = Array.isArray(body.items) ? body.items : [];
  const nextCursor = typeof body.nextCursor === 'string' && body.nextCursor.trim() ? body.nextCursor : null;
  return {
    items,
    nextCursor,
    hasMore: body.hasMore === true && nextCursor !== null,
    ownershipEnforced: body.ownershipEnforced === true,
  };
}

/**
 * A free-text search across the conversations the viewer may see: `GET /api/sessions?q=&cursor=&pageSize=` with NO
 * folder (the server is in keyset mode whenever `q` is non-blank). `q` is required and must be non-blank - a blank query
 * with no folder is the legacy "every conversation" page mode, which has no cursor, so it is refused here rather than
 * sent. Normalised exactly like {@link listSessionsByFolder}.
 */
export async function searchSessions(
  q: string,
  cursor?: string | null,
  pageSize: number = DEFAULT_PAGE_SIZE,
  onlyWithMessages = true,
): Promise<FolderPageResponse> {
  const query = nonBlank(q);
  if (!query) throw new Error('searchSessions: a query is required');

  const params: Record<string, string | number | boolean> = {
    q: query,
    pageSize: clampPageSize(pageSize),
    onlyWithMessages: onlyWithMessages === true,
  };
  const c = nonBlank(cursor);
  if (c) params.cursor = c;

  const res = await axiosClient.get<Partial<FolderPageResponse> | null>(API.SESSIONS.LIST, { params });
  return normaliseFolderPage(res.data);
}

/**
 * One page of ONE customer's conversations - the contact panel's "Past conversations": `GET /api/sessions?customerId=&page=
 * &pageSize=&excludeClosed=false&onlyWithMessages=true` (the server's page mode: no folder, cursor or q, so a page NUMBER
 * and a `hasMore` flag). Closed conversations are included - they are the point - and empty ones are not (an agent has
 * nothing to read in a conversation with no messages). The server applies the same visibility rule as to any list, so a
 * conversation someone else owns privately is simply absent; the console never re-widens that.
 *
 * `customerId` is required and must be non-blank: a blank one would be the whole tenant's conversations. The answer is
 * normalised - `items` is always an array and `hasMore` is true only when the server said exactly `true`.
 */
export async function listCustomerSessions(
  customerId: string,
  page = 1,
  pageSize: number = DEFAULT_PAGE_SIZE,
): Promise<{ items: SessionDto[]; hasMore: boolean }> {
  const customer = nonBlank(customerId);
  if (!customer) throw new Error('listCustomerSessions: a customer id is required');
  const res = await axiosClient.get<{ items?: unknown; hasMore?: unknown } | null>(API.SESSIONS.LIST, {
    params: {
      customerId: customer,
      page: Number.isFinite(page) ? Math.max(1, Math.trunc(page)) : 1,
      pageSize: clampPageSize(pageSize),
      excludeClosed: false,
      onlyWithMessages: true,
    },
  });
  const body = res.data;
  return {
    items: Array.isArray(body?.items) ? (body!.items as SessionDto[]) : [],
    hasMore: body?.hasMore === true,
  };
}

/**
 * ONE row, re-read from the server: `GET /api/sessions/{id}`. Resolves with the row only when the answer is an object
 * that names THIS session (case-insensitive: ids are Guids); anything else - a proxy's HTML page, an answer for some
 * other session - is an error, never "a row". A 404 (the server will not show this row to this viewer, or it is gone)
 * rejects with the axios error untouched, because the CALLER decides what 404 means (the console hides the row).
 */
export async function getSessionDetail(sessionId: string): Promise<SessionDto> {
  const id = nonBlank(sessionId);
  if (!id) throw new Error('getSessionDetail: a session id is required');
  const res = await axiosClient.get<SessionDto | null>(API.SESSIONS.DETAIL(id));
  const dto = res.data;
  if (!dto || typeof dto !== 'object' || typeof dto.sessionId !== 'string' || dto.sessionId.toLowerCase() !== id.toLowerCase()) {
    throw new Error('getSessionDetail: the answer is not this session');
  }
  return dto;
}

/**
 * The folder rail's counts: `GET /api/sessions/counts?groupBy=folder&onlyWithMessages=`. Returns the `counts` map with
 * only finite, non-negative whole numbers kept - a missing key is "the server did not count it" and must not be drawn
 * as 0 (the rail shows no badge for it). Anything that is not the documented object is an empty map.
 */
export async function getSessionCounts(onlyWithMessages = true): Promise<FolderCountsResponse['counts']> {
  const res = await axiosClient.get<Partial<FolderCountsResponse> | null>(API.SESSIONS.COUNTS, {
    params: { groupBy: 'folder', onlyWithMessages: onlyWithMessages === true },
  });
  const raw = res.data?.counts;
  const out: Record<string, number> = {};
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return out;
  for (const [k, v] of Object.entries(raw)) {
    if (typeof v === 'number' && Number.isFinite(v) && v >= 0) out[k] = Math.trunc(v);
  }
  return out;
}

/**
 * The tenant's tag catalog, read-only (`GET /api/sessions/tags?includeInactive=`; usage counts are never requested).
 * A **404 is an empty catalog** (a server that does not have the catalog yet), so the picker is simply empty and chips
 * fall back to the raw key; every other failure rejects. Entries without a string key are dropped; a missing label
 * falls back to the key; an entry counts as active only when the server said `isActive: true`.
 */
export async function getTagCatalog(includeInactive = false): Promise<TagDef[]> {
  let data: unknown;
  try {
    const res = await axiosClient.get<unknown>(API.SESSIONS.TAGS, {
      params: { includeInactive: includeInactive === true },
    });
    data = res.data;
  } catch (err) {
    if ((err as { response?: { status?: number } } | null)?.response?.status === 404) return [];
    throw err;
  }
  if (!Array.isArray(data)) return [];
  const out: TagDef[] = [];
  for (const e of data as Array<Record<string, unknown> | null>) {
    if (!e || typeof e.key !== 'string' || !e.key.trim()) continue;
    out.push({
      key: e.key,
      label: typeof e.label === 'string' && e.label.trim() ? e.label : e.key,
      color: typeof e.color === 'string' ? e.color : '',
      isActive: e.isActive === true,
    });
  }
  return out;
}

/**
 * PR-A (2026-10-26) - GET /api/sessions/console-settings. Rejects on any failure AND on a body that is not the documented
 * shape (a proxy's HTML page, a half-deployed server): the caller treats a rejection as "no settings known" and falls
 * back to the default DISPLAY (tags shown). Nothing about private/public visibility is ever inferred from this - a failed
 * read says nothing about who may see what.
 */
export async function getConsoleSettings(): Promise<ConsoleSettings> {
  const res = await axiosClient.get<unknown>(API.SESSIONS.CONSOLE_SETTINGS);
  const d = res.data as Partial<ConsoleSettings> | null;
  if (!d || typeof d !== 'object' || typeof d.hideTagFolders !== 'boolean' || typeof d.privateConversationsEffective !== 'boolean') {
    throw new Error('console settings response is not the documented shape');
  }
  return {
    tenantId: typeof d.tenantId === 'string' ? d.tenantId : '',
    privateConversations: typeof d.privateConversations === 'boolean' ? d.privateConversations : null,
    privateConversationsEffective: d.privateConversationsEffective,
    privateConversationsInherited: d.privateConversationsInherited === true,
    hideTagFolders: d.hideTagFolders,
    editClosedConversations: d.editClosedConversations === true,
    updatedBy: typeof d.updatedBy === 'string' ? d.updatedBy : null,
    updatedAt: typeof d.updatedAt === 'string' ? d.updatedAt : null,
  };
}

function toTagsResponse(sessionId: string, data: unknown): SessionTagsResponse {
  const d = (data ?? {}) as Partial<SessionTagsResponse>;
  if (!Array.isArray(d.tags) || !d.tags.every((t) => typeof t === 'string')) {
    // Not the documented post-image: do not pretend a change happened. The caller keeps what it had.
    throw new Error('tags response is not {sessionId, tags[], tagsVersion}');
  }
  return {
    sessionId: typeof d.sessionId === 'string' && d.sessionId ? d.sessionId : sessionId,
    tags: d.tags,
    tagsVersion: typeof d.tagsVersion === 'number' && Number.isFinite(d.tagsVersion) ? d.tagsVersion : 0,
  };
}

function requireIds(op: string, sessionId: string, key: string): void {
  if (!nonBlank(sessionId) || !nonBlank(key)) throw new Error(`${op}: a session id and a tag key are required`);
}

/**
 * Attach ONE existing catalog tag (`POST /api/sessions/{id}/tags/{key}`, idempotent). Resolves with the server's
 * post-image `{sessionId, tags, tagsVersion}` - the caller applies THAT, never its own guess. Rejects on any non-2xx
 * (400 unknown_tag, 404 "Session not available" when someone else owns it, 409...), leaving the row untouched.
 */
export async function addSessionTag(sessionId: string, key: string): Promise<SessionTagsResponse> {
  requireIds('addSessionTag', sessionId, key);
  const res = await axiosClient.post<unknown>(API.SESSIONS.TAG(sessionId, key));
  return toTagsResponse(sessionId, res.data);
}

/** Detach ONE tag (`DELETE /api/sessions/{id}/tags/{key}`, idempotent). Same answer and failure contract as {@link addSessionTag}. */
export async function removeSessionTag(sessionId: string, key: string): Promise<SessionTagsResponse> {
  requireIds('removeSessionTag', sessionId, key);
  const res = await axiosClient.delete<unknown>(API.SESSIONS.TAG(sessionId, key));
  return toTagsResponse(sessionId, res.data);
}
